import type { components } from '@social-studio/operator-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call } from '../../lib/api';

export type TenantBilling = components['schemas']['OperatorTenantBillingDto'];
export type JournalEntry = components['schemas']['BillingJournalEntryDto'];
export type Invoice = components['schemas']['BillingInvoiceDto'];
export type AdjustmentReason = components['schemas']['CreateCreditAdjustmentDto']['reason_code'];
export type BillingMode = 'metered' | 'exempt';

/** Query key of the billing answer; the billing change dialog reads the trial end from it. */
export const BILLING_QUERY = 'operator-tenant-billing';

export const ADJUSTMENT_REASONS: readonly AdjustmentReason[] = ['goodwill', 'correction', 'compensation', 'promotion', 'other'];

/** Billing of one tenant from the license platform (REQ-140-014); each load is audited, so no refetch on remount. */
export function useTenantBilling(id: string) {
  return useQuery({
    queryKey: [BILLING_QUERY, id],
    queryFn: () => call(() => api.GET('/v1/operator/tenants/{id}/billing', { params: { path: { id } } })),
    staleTime: 5 * 60_000,
  });
}

/** Journal pages after the first one the billing answer carries. */
export function useBillingJournal(id: string, firstCursor: string | null) {
  return useInfiniteQuery({
    queryKey: ['operator-tenant-billing-journal', id, firstCursor],
    initialPageParam: firstCursor ?? '',
    enabled: false,
    queryFn: ({ pageParam }) =>
      call(() => api.GET('/v1/operator/tenants/{id}/billing/journal', { params: { path: { id }, query: { limit: 50, cursor: pageParam } } })),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
}

export interface AdjustmentInput {
  readonly amount: number;
  readonly reasonCode: AdjustmentReason;
  readonly reason: string;
  readonly expiresAt: string | null;
  /** One key per opened dialog: a repeated click books once (REQ-140-020). */
  readonly key: string;
}

export interface BillingChangeInput {
  readonly billingMode?: BillingMode;
  readonly trialEndsAt?: string;
  readonly reason: string;
}

/** Credit adjustments and billing changes; each refreshes billing, the journal and the tenant's audit. */
export function useBillingActions(id: string) {
  const client = useQueryClient();
  const refresh = () =>
    Promise.all(
      [BILLING_QUERY, 'operator-tenant-billing-journal', 'operator-tenant-audit', 'operator-tenant'].map((key) => client.invalidateQueries({ queryKey: [key, id] })),
    );
  const path = { params: { path: { id } } };
  return {
    adjust: useMutation({
      mutationFn: (input: AdjustmentInput) =>
        call(() =>
          api.POST('/v1/operator/tenants/{id}/credit-adjustments', {
            params: { path: { id }, header: { 'Idempotency-Key': input.key } },
            body: { amount: input.amount, reason_code: input.reasonCode, reason: input.reason, ...(input.expiresAt ? { expires_at: input.expiresAt } : {}) },
          }),
        ),
      onSuccess: refresh,
    }),
    change: useMutation({
      mutationFn: (input: BillingChangeInput) =>
        call(() =>
          api.PATCH('/v1/operator/tenants/{id}', {
            ...path,
            body: { ...(input.billingMode ? { billing_mode: input.billingMode } : {}), ...(input.trialEndsAt ? { trial_ends_at: input.trialEndsAt } : {}), reason: input.reason },
          }),
        ),
      onSuccess: refresh,
    }),
  };
}
